import { Parser } from '~/api';
import { Egg, Tag } from '~/api/models';
import type { AdditionalPort, AdditionalPortFailMode, ConfigEditorFile, EggFirewallConfig, ParsedConfigFile } from '~/api/models/Egg';
import { FavoriteFile } from '~/composables/files/useFavorites';
import { useModelsStore } from '~/stores/models';
import RequestService from './request';

interface CreateEggRequest {
    name: string;
    tag: string;
    description: string;
    docker_images: Record<string, string>;
    startup: string;
    config_from: number;
    config_logs: string;
    config_files: string;
    config_startup: string;
    config_stop: string;
    custom_config: string;
    wispignore: string;
    console_filters: string;
    favorite_files: FavoriteFile[];
    firewall_config?: EggFirewallConfig | null;
    min_cpu: number | null;
    min_memory: number | null;
    min_disk: number | null;
    recommended_cpu: number | null;
    recommended_memory: number | null;
    recommended_disk: number | null;
    additional_ports: AdditionalPort[];
    additional_port_fail_mode: AdditionalPortFailMode;
    tags?: string[];
}

interface ImportUpdateEggRequest {
    import_file: File;
}

interface UpdateScriptsRequest {
    scriptInstall: string;
    scriptIsPrivileged: boolean;
    scriptEntry: string;
    scriptContainer: string;
    copyScriptFrom?: number;

    scriptUpdate: string;
    scriptUpdateIsPrivileged: boolean;
    scriptUpdateEntry: string;
    scriptUpdateContainer: string;
    copyUpdateScriptFrom?: number;
}

interface AssetRequest {
    asset: File;
}

interface UpdateExportDataRequest {
    export_data: Record<string, unknown> | null;
}

interface UpdateSwitchConfigRequest {
    switch_disabled: boolean;
    switch_group_ids: string[];
    allowed_node_ids: string[];
    allowed_location_ids: string[];
    allow_cross_location_switch: boolean;
}

class EggsService {
    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/eggs', {
            include: ['serversCount', 'tags'],
            ...req,
        }).then(Parser.parse);
    }

    getAllAcrossNests(req: PaginatableRequest): Promise<ListResponse> {
        return this.getAll(req);
    }

    /**
     * Load every egg by walking the pages — the grouped grid view buckets eggs by
     * tag client-side, so it needs the full set at once (per_page is capped at 50
     * server-side). Mirrors TagsService.getAllForPicker.
     */
    async getAllForGrouping(): Promise<Egg[]> {
        const collected: Egg[] = [];
        let page = 1;
        let totalPages = 1;

        do {
            const response = await this.getAll({ perPage: 50, page } as PaginatableRequest);
            collected.push(...((response.data ?? []) as unknown as Egg[]));
            totalPages = response.meta?.pagination?.totalPages ?? 1;
            page++;
        } while (page <= totalPages);

        return collected;
    }

    getAllForSelector(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/eggs', {
            sort: 'name',
            ...req,
        }).then(Parser.parse);
    }

    get(): Promise<Egg> {
        return RequestService.get('/eggs/:egg', {
            include: ['tags'],
        }).then(Parser.parse);
    }

    syncTags(data: { tags: string[] }): Promise<Tag[]> {
        return RequestService.put('/eggs/:egg/tags', data)
            .then(Parser.parse)
            .then((tags) => {
                useModelsStore().refreshModel('egg');

                return tags;
            });
    }

    create(data: CreateEggRequest): Promise<Egg> {
        return RequestService.post('/eggs?include=tags', data).then(Parser.parse);
    }

    importStandalone(data: { import_file: File; tags?: string[] }) {
        const formData = new FormData();
        formData.append('import_file', data.import_file);
        (data.tags ?? []).forEach((tag) => formData.append('tags[]', tag));

        return RequestService.post('/eggs/import?include=tags', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(Parser.parse);
    }

    importUpdate(data: ImportUpdateEggRequest) {
        const formData = new FormData();
        formData.append('import_file', data.import_file);

        return RequestService.post('/eggs/:egg/import?include=tags', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(Parser.parse)
            .then((egg) => {
                useModelsStore().refreshModel('egg');

                return egg;
            });
    }

    update(data: CreateEggRequest): Promise<Egg> {
        return RequestService.put('/eggs/:egg?include=tags', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateScripts(data: UpdateScriptsRequest): Promise<Egg> {
        return RequestService.put('/eggs/:egg/scripts', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    private assetToFormData(data: AssetRequest) {
        const formData = new FormData();
        if (data.asset) formData.append('asset', data.asset);
        return formData;
    }

    updateThumbnail(data: AssetRequest): Promise<Egg> {
        return RequestService.post('/eggs/:egg/thumbnail', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(Parser.parse)
            .then(RequestService.updateModelBinding);
    }

    updateConfigEditor(data: { config_editor: ConfigEditorFile[] }): Promise<Egg> {
        return RequestService.put('/eggs/:egg/config-editor', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    /**
     * Turn a pasted config file into the keys it holds, so an admin picks from a
     * list instead of typing identities such as `Options::MaxPlayers` or `exec#2`
     * from memory. Parsed by the same code the editor itself uses, so what comes
     * back is exactly what a customer would see.
     */
    parseConfigFile(data: { path: string; contents: string; expand_structs?: boolean }): Promise<ParsedConfigFile> {
        return RequestService.post('/eggs/:egg/config-editor/parse', data);
    }

    updateFirewallConfig(data: { firewall_config: EggFirewallConfig | null }): Promise<Egg> {
        return RequestService.put('/eggs/:egg/firewall-config', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    syncFirewallToServers(): Promise<Egg> {
        return RequestService.post('/eggs/:egg/firewall-config/sync').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    delete(): Promise<void> {
        return RequestService.delete('/eggs/:egg');
    }

    syncFromSource(): Promise<Egg> {
        return RequestService.post('/eggs/:egg/sync-from-source').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    detachFromSource(): Promise<Egg> {
        return RequestService.post('/eggs/:egg/detach-from-source').then(Parser.parse).then(RequestService.updateModelBinding);
    }

    attachToSource(data: { source_key: string; source_id: string }): Promise<Egg> {
        return RequestService.post('/eggs/:egg/attach-to-source', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateExportData(data: UpdateExportDataRequest): Promise<Egg> {
        return RequestService.put('/eggs/:egg/export-data', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    updateSwitchConfig(data: UpdateSwitchConfigRequest): Promise<Egg> {
        return RequestService.put('/eggs/:egg/switch-config', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }
}

export default new EggsService();
